import React, { useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Camera, Upload, ImagePlus, Trash2, X, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

const MAX_RAW_BYTES = 8 * 1024 * 1024; // 8MB raw input cap (we compress after)
const TARGET_PX = 512; // square output edge
const JPEG_QUALITY = 0.85;

/**
 * Reads a File and crops it to a centered square, resized to TARGET_PX, compressed JPEG.
 * Returns a new File (image/jpeg) ready to upload.
 */
async function compressSquare(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const side = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - side) / 2;
  const sy = (img.naturalHeight - side) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = TARGET_PX;
  canvas.height = TARGET_PX;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, side, side, 0, 0, TARGET_PX, TARGET_PX);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
  );
  if (!blob) throw new Error(tfr("Compression échouée"));
  return new File([blob], "avatar.jpg", { type: "image/jpeg" });
}

/**
 * AvatarUpload — interactive avatar with camera overlay.
 * Click → bottom sheet (mobile) / centered modal (desktop) with options:
 *   • Prendre une photo (camera)
 *   • Choisir depuis la galerie
 *   • Supprimer la photo (if photo_url)
 *
 * Drag & drop on desktop also supported.
 */
export default function AvatarUpload({
  player,
  onUpdated,
  size = "md", // "md" 64px, "lg" 80px (mobile/desktop scaling handled by caller)
  className,
}) {
  const { tf } = useLang();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const initials = (player?.name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const photoSrc =
    preview ||
    player?.photo_url ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(player?.name || "")}&backgroundColor=1f2937&textColor=ffffff`;

  const sizeCls =
    size === "lg"
      ? "h-20 w-20 md:h-24 md:w-24"
      : size === "sm"
        ? "h-11 w-11 md:h-14 md:w-14"
        : "h-16 w-16 md:h-20 md:w-20";

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(tf("Le fichier doit être une image"));
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      toast.error(tf("Image trop volumineuse (max 8 Mo)"));
      return;
    }
    setOpen(false);
    setUploading(true);
    let tempUrl = null;
    try {
      const compressed = await compressSquare(file);
      // Optimistic preview
      tempUrl = URL.createObjectURL(compressed);
      setPreview(tempUrl);
      const fd = new FormData();
      fd.append("file", compressed, compressed.name);
      const res = await api.post(`/players/${player.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Replace preview with persisted URL via parent reload
      toast.success(tf("Photo mise à jour"));
      onUpdated?.(res.data?.photo_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Échec de la mise à jour"));
      setPreview(null);
    } finally {
      setUploading(false);
      if (tempUrl) {
        // Revoke shortly after to allow render swap
        setTimeout(() => URL.revokeObjectURL(tempUrl), 800);
      }
      // Clear native input value so re-selecting the same file fires onChange
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    setOpen(false);
    setUploading(true);
    try {
      await api.delete(`/players/${player.id}/avatar`);
      toast.success(tf("Photo supprimée"));
      setPreview(null);
      onUpdated?.("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Suppression impossible"));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <>
      <div
        className={cn("relative shrink-0 group", className)}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="avatar-upload"
      >
        <button
          type="button"
          onClick={() => !uploading && setOpen(true)}
          disabled={uploading}
          className={cn(
            "relative rounded-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40 transition-all duration-200",
            "hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]",
            dragOver && "ring-2 ring-info/60"
          )}
          aria-label={tf("Modifier la photo de profil")}
          data-testid="avatar-upload-trigger"
        >
          <Avatar className={cn(sizeCls, "border-2 border-white/10")}>
            <AvatarImage src={photoSrc} alt={player?.name || "joueur"} />
            <AvatarFallback className="bg-secondary text-base">{initials}</AvatarFallback>
          </Avatar>

          {/* Loading overlay */}
          {uploading && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[1px]"
              data-testid="avatar-uploading"
            >
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            </div>
          )}
        </button>

        {/* Camera overlay (bottom-right) */}
        {!uploading && (
          <span
            className={cn(
              "pointer-events-none absolute -bottom-0.5 -right-0.5 h-7 w-7 md:h-8 md:w-8 rounded-full flex items-center justify-center",
              "bg-white border border-black/[0.12] shadow-[0_1px_4px_rgba(15,23,42,0.28)]",
              "dark:bg-[#0E1115]/90 dark:border-white/15 dark:shadow-md dark:backdrop-blur-sm",
              "transition-all duration-200 group-hover:scale-110",
              "group-hover:border-black/25 dark:group-hover:border-white/30",
              "group-active:scale-100"
            )}
            aria-hidden
          >
            <Camera className="h-3.5 w-3.5 md:h-4 md:w-4 text-foreground" />
          </span>
        )}
      </div>

      {/* Hidden inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="avatar-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="avatar-gallery-input"
      />

      <AvatarOptionsSheet
        open={open}
        onOpenChange={setOpen}
        hasPhoto={!!player?.photo_url}
        onTakePhoto={() => cameraRef.current?.click()}
        onPickGallery={() => galleryRef.current?.click()}
        onDelete={handleDelete}
      />
    </>
  );
}

function AvatarOptionsSheet({ open, onOpenChange, hasPhoto, onTakePhoto, onPickGallery, onDelete }) {
  const { tf } = useLang();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          data-testid="avatar-sheet"
          className={cn(
            "fixed z-50 bg-[#0E1115] border border-white/10 shadow-2xl flex flex-col overflow-hidden",
            "left-0 right-0 bottom-0 rounded-t-3xl pb-[env(safe-area-inset-bottom)]",
            "md:left-1/2 md:right-auto md:bottom-auto md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
            "md:w-[90vw] md:max-w-sm md:rounded-3xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "md:data-[state=closed]:slide-out-to-bottom-0 md:data-[state=open]:slide-in-from-bottom-0",
            "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95"
          )}
        >
          {/* Drag handle (mobile) */}
          <div className="md:hidden flex justify-center pt-2 pb-1">
            <div className="h-1 w-10 rounded-full bg-white/15" aria-hidden />
          </div>

          <header className="flex items-center justify-between px-5 pt-3 md:pt-5 pb-3 border-b border-white/[0.06]">
            <DialogPrimitive.Title className="text-base font-semibold tracking-tight">
              {tf("Photo du joueur")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              {tf("Choisir une nouvelle photo de profil ou supprimer celle existante.")}
            </DialogPrimitive.Description>
            <DialogPrimitive.Close
              className="rounded-full h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors"
              data-testid="avatar-sheet-close"
              aria-label={tf("Fermer")}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </header>

          <div className="px-3 py-3 space-y-1">
            <SheetItem
              icon={Camera}
              label={tf("Prendre une photo")}
              hint={tf("Caméra de l'appareil")}
              onClick={onTakePhoto}
              testid="avatar-action-camera"
            />
            <SheetItem
              icon={ImagePlus}
              label={tf("Choisir depuis la galerie")}
              hint={tf("Image existante (jpg, png, webp)")}
              onClick={onPickGallery}
              testid="avatar-action-gallery"
            />
            {hasPhoto && (
              <SheetItem
                icon={Trash2}
                label={tf("Supprimer la photo")}
                hint={tf("Reviendra aux initiales")}
                onClick={onDelete}
                tone="danger"
                testid="avatar-action-delete"
              />
            )}
          </div>

          <div className="hidden md:flex items-center gap-2 mx-5 mb-5 mt-1 rounded-2xl border border-dashed border-white/10 px-4 py-3 text-[11px] text-muted-foreground">
            <Upload className="h-3.5 w-3.5" />
            {tf("Astuce : glissez-déposez une image directement sur l'avatar.")}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function SheetItem({ icon: Icon, label, hint, onClick, tone, testid }) {
  const danger = tone === "danger";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors text-left",
        "hover:bg-white/[0.04] active:bg-white/[0.07]",
        danger && "hover:bg-danger/10"
      )}
      data-testid={testid}
    >
      <span
        className={cn(
          "h-10 w-10 rounded-xl flex items-center justify-center border shrink-0",
          danger
            ? "bg-danger/10 border-danger/25 text-danger"
            : "bg-white/[0.04] border-white/10 text-foreground"
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={cn(
            "block text-sm font-medium truncate",
            danger && "text-danger"
          )}
        >
          {label}
        </span>
        <span className="block text-[11px] text-muted-foreground truncate">{hint}</span>
      </span>
    </button>
  );
}
